import { useEffect, useRef, useState } from "react";
import { formatScore, periodDisplayLabel, toNumber } from "./historyModel";

// Cross-period trend of CVI, Individual Contribution and Collective Contribution.
// Lines are drawn only between two recorded results: a missing value leaves a gap and is
// never interpolated. Provisional (active-period) points are hollow and joined with dashes.

const SERIES = [
    { key: "cvi", color: "#2f2fb4", marker: "circle", width: 2.75 },
    { key: "individual", color: "#00879c", marker: "square", width: 2 },
    { key: "org", color: "#8a4fc8", marker: "diamond", width: 2 },
];
const HEIGHT = 268;
const PAD = { top: 18, right: 20, bottom: 36, left: 40 };
const TICKS = [0, 25, 50, 75, 100];
// Keeps the first and last period labels inside the chart.
const INSET = 36;

function Marker({ type, x, y, color, hollow, size = 4.5 }) {
    const fill = hollow ? "#fff" : color;
    if (type === "square") return <rect x={x - size} y={y - size} width={size * 2} height={size * 2} rx={1.5} fill={fill} stroke={color} strokeWidth={2} />;
    if (type === "diamond") return <path d={`M${x} ${y - size - 1} L${x + size + 1} ${y} L${x} ${y + size + 1} L${x - size - 1} ${y} Z`} fill={fill} stroke={color} strokeWidth={2} />;
    return <circle cx={x} cy={y} r={size} fill={fill} stroke={color} strokeWidth={2} />;
}

export default function HistoryTrendChart({ periods = [], selectedId = null, onSelect, c, language = "en" }) {
    const wrapRef = useRef(null);
    const [width, setWidth] = useState(720);
    const [hover, setHover] = useState(null);
    useEffect(() => {
        const node = wrapRef.current;
        if (!node) return undefined;
        const measure = () => setWidth(Math.max(320, Math.round(node.clientWidth || 720)));
        measure();
        if (typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const t = c.trend;
    const labels = { cvi: t.cvi, individual: t.individual, org: t.collective };
    const hasAny = periods.some(p => SERIES.some(s => toNumber(p[s.key]) != null));
    if (!periods.length || !hasAny) return <div className="cvi-history-empty">{t.empty}</div>;

    const innerW = width - PAD.left - PAD.right;
    const innerH = HEIGHT - PAD.top - PAD.bottom;
    const step = periods.length > 1 ? (innerW - INSET * 2) / (periods.length - 1) : 0;
    const xAt = i => PAD.left + (periods.length > 1 ? INSET + i * step : innerW / 2);
    const yAt = v => PAD.top + innerH - (Math.max(0, Math.min(100, v)) / 100) * innerH;
    const colW = periods.length > 1 ? step : innerW;
    const bandW = Math.min(colW, 120);
    const bandX = i => Math.max(PAD.left, Math.min(width - PAD.right - bandW, xAt(i) - bandW / 2));
    const hovered = hover != null ? periods[hover] : null;

    return <div className="cvi-history-trend">
        <ul className="cvi-history-legend" aria-hidden="true">
            {SERIES.map(s => <li key={s.key}><svg width="22" height="12"><line x1="1" y1="6" x2="21" y2="6" stroke={s.color} strokeWidth={s.width} /><Marker type={s.marker} x={11} y={6} color={s.color} size={3.5} /></svg>{labels[s.key]}</li>)}
            <li className="is-muted"><svg width="22" height="12"><line x1="1" y1="6" x2="21" y2="6" stroke="#8b93a7" strokeWidth="2" strokeDasharray="4 3" /><circle cx="11" cy="6" r="3.5" fill="#fff" stroke="#8b93a7" strokeWidth="2" /></svg>{t.provisional}</li>
        </ul>
        <div className="cvi-history-chart" ref={wrapRef}>
            <svg width={width} height={HEIGHT} role="img" aria-label={`${t.title}. ${t.selectHint}`}>
                {TICKS.map(v => <g key={v}>
                    <line x1={PAD.left} x2={width - PAD.right} y1={yAt(v)} y2={yAt(v)} stroke={v === 0 ? "#d3d8e3" : "#edeff4"} />
                    <text x={PAD.left - 10} y={yAt(v) + 4} textAnchor="end" className="cvi-history-axis">{v}</text>
                </g>)}
                {periods.map((p, i) => {
                    const selected = String(p.periodId) === String(selectedId);
                    return <g key={`band-${p.periodId}`}>
                        {selected && <rect x={bandX(i)} y={PAD.top - 6} width={bandW} height={innerH + 12} rx={8} fill="#f0f1fb" />}
                        <text x={xAt(i)} y={HEIGHT - 12} textAnchor="middle" className={`cvi-history-axis is-x${selected ? " is-selected" : ""}`}>
                            {periodDisplayLabel(p, language)}{p._state === "provisional" ? " *" : ""}
                        </text>
                    </g>;
                })}
                {SERIES.map(s => periods.map((p, i) => {
                    if (i === 0) return null;
                    const a = toNumber(periods[i - 1][s.key]), b = toNumber(p[s.key]);
                    if (a == null || b == null) return null;
                    const dashed = periods[i - 1]._state === "provisional" || p._state === "provisional";
                    return <line key={`${s.key}-${i}`} x1={xAt(i - 1)} y1={yAt(a)} x2={xAt(i)} y2={yAt(b)} stroke={s.color} strokeWidth={s.width}
                        strokeDasharray={dashed ? "6 5" : undefined} strokeLinecap="round" />;
                }))}
                {SERIES.map(s => periods.map((p, i) => {
                    const v = toNumber(p[s.key]);
                    if (v == null) return null;
                    return <Marker key={`${s.key}-m-${i}`} type={s.marker} x={xAt(i)} y={yAt(v)} color={s.color} hollow={p._state === "provisional"} />;
                }))}
                {periods.map((p, i) => {
                    const v = toNumber(p.cvi);
                    return v == null ? null : <text key={`v-${i}`} x={xAt(i)} y={yAt(v) - 11} textAnchor="middle" className="cvi-history-value">{formatScore(v, language)}</text>;
                })}
                {periods.map((p, i) => <rect key={`hit-${p.periodId}`} x={xAt(i) - colW / 2} y={PAD.top - 6} width={colW} height={innerH + 34}
                    fill="transparent" className="cvi-history-hit" tabIndex={0} role="button"
                    aria-label={`${periodDisplayLabel(p, language)} · ${p._state === "final" ? t.final : t.provisional} · ${t.cvi} ${formatScore(p.cvi, language)} · ${t.individual} ${formatScore(p.individual, language)} · ${t.collective} ${formatScore(p.org, language)}`}
                    aria-pressed={String(p.periodId) === String(selectedId)}
                    onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                    onFocus={() => setHover(i)} onBlur={() => setHover(null)}
                    onClick={() => onSelect?.(p.periodId)}
                    onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onSelect?.(p.periodId); } }} />)}
            </svg>
            {hovered && <div className="cvi-history-tooltip" role="presentation"
                style={{ left: Math.min(Math.max(xAt(hover), 110), width - 110), top: 8 }}>
                <strong>{periodDisplayLabel(hovered, language)}</strong>
                <span className={`cvi-history-state is-${hovered._state}`}>{hovered._state === "final" ? t.final : t.provisional}</span>
                <dl>{SERIES.map(s => <div key={s.key}><dt><i style={{ background: s.color }} />{labels[s.key]}</dt><dd>{toNumber(hovered[s.key]) == null ? t.noData : formatScore(hovered[s.key], language)}</dd></div>)}</dl>
            </div>}
        </div>
        <div className="cvi-history-sr"><table>
            <caption>{t.tableCaption}</caption>
            <thead><tr><th scope="col">{c.table.period}</th><th scope="col">{c.table.status}</th>{SERIES.map(s => <th key={s.key} scope="col">{labels[s.key]}</th>)}</tr></thead>
            <tbody>{periods.map(p => <tr key={p.periodId}><th scope="row">{periodDisplayLabel(p, language)}</th><td>{p._state === "final" ? t.final : t.provisional}</td>
                {SERIES.map(s => <td key={s.key}>{toNumber(p[s.key]) == null ? t.noData : formatScore(p[s.key], language)}</td>)}</tr>)}</tbody>
        </table></div>
    </div>;
}
